// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Metric Sync Engine
// Refreshes each milestone's metric_current value from live business data,
// then updates the consecutive-months streak counter.
//
// This is what makes the Growth Tree "live" — as your business improves,
// milestones auto-progress or regress without manual intervention.
//
// Called by GrowthRoadmapPage on mount + via a "Sync now" button.
// ══════════════════════════════════════════════════════════════════════════════

import { supabase } from '@lib/supabase';

// ─── Metric computers ─────────────────────────────────────────────────
// Each function takes tenant data, returns the current value for that metric.
// Maps 1:1 to the `metric_type` column on growth_milestones.

const METRIC_COMPUTERS = {
  /**
   * Cancellation % = total cancellations / total transactions × 100
   * (averaged across all pos_employees)
   */
  cancellation_percentage: (data) => {
    const emps = data.employees || [];
    const totalCancels = emps.reduce((s, e) => s + (e.total_cancellations || 0), 0);
    const totalTx = emps.reduce((s, e) => s + (e.total_transactions || 0), 0);
    return totalTx > 0 ? (totalCancels / totalTx) * 100 : 0;
  },

  /**
   * Gross margin % = weighted by selling price × quantity
   * (simple version: average margin across all active products)
   */
  gross_margin: (data) => {
    const products = (data.products || []).filter(p => p.is_active !== false);
    if (!products.length) return 0;
    const sum = products.reduce((s, p) => s + parseFloat(p.profit_margin || 0), 0);
    return sum / products.length;
  },

  /**
   * Open gaps count — direct from gaps data
   */
  open_gaps: (data) => {
    const gaps = data.gaps_raw || data.gaps || [];
    return gaps.filter(g => !g.is_resolved).length;
  },

  /**
   * Purchase reduction % — compare last 30d vs previous 30d of invoices
   */
  purchase_reduction: (data) => {
    const invoices = data.invoices || [];
    if (invoices.length < 10) return 0;
    const now = new Date();
    const d30 = new Date(now.getTime() - 30 * 86400000);
    const d60 = new Date(now.getTime() - 60 * 86400000);

    const recent = invoices.filter(i => new Date(i.invoice_date) >= d30)
      .reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);
    const prev = invoices.filter(i => {
      const dt = new Date(i.invoice_date);
      return dt >= d60 && dt < d30;
    }).reduce((s, i) => s + parseFloat(i.total_amount || 0), 0);

    if (prev === 0) return 0;
    return ((prev - recent) / prev) * 100;
  },

  /**
   * Repeat customer rate — placeholder until customer tracking is built
   * Uses product diversity as proxy (more diverse products = more loyal base)
   */
  repeat_rate: (data) => {
    // Placeholder: proxy = (avg_daily_orders / unique_customers_per_day)
    // Since we don't track per-customer yet, estimate from order variability
    const sales = (data.sales || []).slice(0, 30);
    if (!sales.length) return 0;
    const avgOrders = sales.reduce((s, d) => s + (d.total_orders || 0), 0) / sales.length;
    // Heuristic: if avg orders > 80/day, assume 35-40% repeat rate
    return Math.min(50, avgOrders * 0.45);
  },

  /**
   * Avg honesty score across active employees
   */
  avg_honesty: (data) => {
    const emps = (data.employees || []).filter(e => e.is_active !== false);
    if (!emps.length) return 0;
    const sum = emps.reduce((s, e) => s + parseFloat(e.honesty_score || 0), 0);
    return sum / emps.length;
  },

  /**
   * Branch count
   */
  branch_count: (data) => {
    return (data.branches || []).filter(b => b.is_active !== false).length;
  }
};

/**
 * Compare a metric value against its target given an operator.
 */
function isTargetMet(value, target, operator) {
  const v = parseFloat(value || 0);
  const t = parseFloat(target || 0);
  switch (operator) {
    case 'gte': return v >= t;
    case 'lte': return v <= t;
    case 'eq':  return v === t;
    default:    return false;
  }
}

/**
 * Compute consecutive-months streak from history, working backward from current month.
 */
function computeStreak(history) {
  // history: array of { year_month, target_met } sorted desc
  let streak = 0;
  for (const row of history) {
    if (row.target_met) streak++;
    else break;
  }
  return streak;
}

/**
 * Main sync: for each milestone, compute current metric, upsert history row,
 * recompute streak, update milestone row.
 *
 * Returns a summary: { synced, streakUpdates, errors }
 */
export async function syncMilestoneMetrics({ tenantId, data }) {
  if (!tenantId || !data) {
    return { synced: 0, streakUpdates: 0, errors: ['missing_tenant_or_data'] };
  }

  const milestones = data.milestones || [];
  if (!milestones.length) {
    return { synced: 0, streakUpdates: 0, errors: [] };
  }

  const now = new Date();
  const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  let synced = 0;
  let streakUpdates = 0;
  const errors = [];

  for (const milestone of milestones) {
    if (milestone.is_completed) continue;
    if (!milestone.metric_type || !METRIC_COMPUTERS[milestone.metric_type]) {
      continue; // unsupported metric type — skip silently
    }

    try {
      // 1. Compute current value
      const computedValue = METRIC_COMPUTERS[milestone.metric_type](data);
      const targetMet = isTargetMet(computedValue, milestone.metric_target, milestone.metric_operator);

      // 2. Upsert current month history row
      await supabase
        .from('milestone_metric_history')
        .upsert({
          milestone_id: milestone.id,
          tenant_id: tenantId,
          year_month: currentYearMonth,
          metric_value: computedValue,
          target_value: milestone.metric_target,
          target_operator: milestone.metric_operator
        }, { onConflict: 'milestone_id,year_month' });

      // 3. Fetch recent history (last 12 months) to compute streak
      const { data: history } = await supabase
        .from('milestone_metric_history')
        .select('year_month, target_met')
        .eq('milestone_id', milestone.id)
        .order('year_month', { ascending: false })
        .limit(12);

      const newStreak = computeStreak(history || []);
      const oldStreak = parseInt(milestone.consecutive_months_achieved || 0);

      // 4. Update milestone row
      const { error: updErr } = await supabase
        .from('growth_milestones')
        .update({
          metric_current: computedValue,
          consecutive_months_achieved: newStreak,
          last_metric_sync: now.toISOString()
        })
        .eq('id', milestone.id);

      if (updErr) {
        errors.push(`${milestone.metric_type}: ${updErr.message}`);
        continue;
      }

      synced++;
      if (newStreak !== oldStreak) streakUpdates++;
    } catch (err) {
      errors.push(`${milestone.metric_type}: ${err.message}`);
    }
  }

  return { synced, streakUpdates, errors };
}
